import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Combobox } from '../components/ui/combobox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  PackageCheck, Search, Plus, ArrowLeft, Loader2, Check, Undo2,
  Pill, Box, AlertTriangle, User, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const Aprovisionamento = () => {
  const { api } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Detail view
  const [selectedItem, setSelectedItem] = useState(null);
  const [detalhes, setDetalhes] = useState(null);
  const [detLoading, setDetLoading] = useState(false);

  // Provision modal
  const [showProv, setShowProv] = useState(false);
  const [provForm, setProvForm] = useState({ item_id: '', internacao_id: '', quantidade: '', observacoes: '' });
  const [savingProv, setSavingProv] = useState(false);
  const [internacoes, setInternacoes] = useState([]);

  // Return modal
  const [showDevolver, setShowDevolver] = useState(null);
  const [devolverQty, setDevolverQty] = useState('');
  const [devolverObs, setDevolverObs] = useState('');

  // All estoque items (for marking as aprovisionavel)
  const [allEstoqueItems, setAllEstoqueItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [addItemId, setAddItemId] = useState('');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/aprovisionamento/itens');
      setItems(res.data || []);
    } catch { toast.error('Erro ao carregar itens'); }
    finally { setLoading(false); }
  }, [api]);

  const fetchInternacoes = useCallback(async () => {
    try {
      const res = await api.get('/internacoes');
      setInternacoes((res.data || []).filter(i => i.status_kanban !== 'alta_efetivada'));
    } catch { /* ignore */ }
  }, [api]);

  const fetchAllEstoque = useCallback(async () => {
    try {
      const res = await api.get('/estoque/itens');
      setAllEstoqueItems((res.data || []).filter(i => i.ativo !== false));
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchItems(); fetchInternacoes(); }, [fetchItems, fetchInternacoes]);

  const fetchDetalhes = async (itemId) => {
    setDetLoading(true);
    try {
      const res = await api.get(`/aprovisionamento/item/${itemId}/detalhes`);
      setDetalhes(res.data);
      setSelectedItem(itemId);
    } catch { toast.error('Erro ao carregar detalhes'); }
    finally { setDetLoading(false); }
  };

  const handleAprovisionar = async () => {
    if (!provForm.item_id || !provForm.internacao_id || !provForm.quantidade) return;
    setSavingProv(true);
    try {
      const res = await api.post('/aprovisionamento/aprovisionar', {
        item_id: provForm.item_id,
        internacao_id: provForm.internacao_id,
        quantidade: parseFloat(provForm.quantidade),
        observacoes: provForm.observacoes || null,
      });
      toast.success(res.data.message);
      setShowProv(false);
      setProvForm({ item_id: '', internacao_id: '', quantidade: '', observacoes: '' });
      fetchItems();
      if (selectedItem) fetchDetalhes(selectedItem);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aprovisionar');
    } finally { setSavingProv(false); }
  };

  const handleDevolver = async () => {
    if (!showDevolver || !devolverQty) return;
    try {
      const res = await api.post(`/aprovisionamento/${showDevolver}/devolver`, {
        quantidade: parseFloat(devolverQty),
        observacoes: devolverObs || null,
      });
      toast.success(res.data.message);
      setShowDevolver(null);
      setDevolverQty('');
      setDevolverObs('');
      fetchItems();
      if (selectedItem) fetchDetalhes(selectedItem);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao devolver');
    }
  };

  const handleAddAprovisionavel = async () => {
    if (!addItemId) return;
    try {
      await api.put(`/aprovisionamento/item/${addItemId}/aprovisionavel?ativo=true`);
      toast.success('Item marcado como aprovisionável');
      setShowAddItem(false);
      setAddItemId('');
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const handleRemoveAprovisionavel = async (itemId) => {
    if (!confirm('Remover este item da lista de aprovisionáveis?')) return;
    try {
      await api.put(`/aprovisionamento/item/${itemId}/aprovisionavel?ativo=false`);
      toast.success('Item removido');
      fetchItems();
      if (selectedItem === itemId) { setSelectedItem(null); setDetalhes(null); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const filtered = items.filter(i =>
    i.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (i.sku || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pacienteOptions = internacoes.map(i => ({
    value: i.id,
    label: i.paciente_nome || 'Paciente',
  }));

  const itemOptions = items.map(i => ({
    value: i.id,
    label: `${i.nome} (Saldo: ${i.saldo_disponivel} ${i.unidade})`,
    subtitle: i.sku,
  }));

  const totalEstoque = items.reduce((s, i) => s + i.estoque_total, 0);
  const totalProv = items.reduce((s, i) => s + i.aprovisionado, 0);
  const totalSaldo = items.reduce((s, i) => s + i.saldo_disponivel, 0);

  return (
    <Layout>
      <div className="space-y-6 p-4 sm:p-6" data-testid="aprovisionamento-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <PackageCheck className="h-6 w-6 text-primary" />
              Aprovisionamento
            </h1>
            <p className="text-muted-foreground">Reserva de estoque para pacientes internados</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-2" onClick={() => { fetchAllEstoque(); setShowAddItem(true); setAddItemId(''); }}
              data-testid="btn-add-aprovisionavel">
              <Plus className="h-4 w-4" /> Adicionar Item
            </Button>
            <Button className="gap-2 bg-primary" onClick={() => { setProvForm({ item_id: '', internacao_id: '', quantidade: '', observacoes: '' }); setShowProv(true); }}
              data-testid="btn-aprovisionar">
              <PackageCheck className="h-4 w-4" /> Aprovisionar
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <Card><CardContent className="pt-4 text-center">
            <p className="text-xs text-muted-foreground">Estoque Total</p>
            <p className="text-2xl font-bold" data-testid="total-estoque">{totalEstoque}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 text-center">
            <p className="text-xs text-muted-foreground">Aprovisionado p/ Internados</p>
            <p className="text-2xl font-bold text-amber-600" data-testid="total-aprovisionado">{totalProv}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 text-center">
            <p className="text-xs text-muted-foreground">Saldo Disponível</p>
            <p className="text-2xl font-bold text-green-600" data-testid="total-saldo">{totalSaldo}</p>
          </CardContent></Card>
        </div>

        {selectedItem && detalhes ? (
          /* Detail view */
          <>
            <Button variant="outline" size="sm" onClick={() => { setSelectedItem(null); setDetalhes(null); }} className="gap-1">
              <ArrowLeft className="h-4 w-4" /> Voltar
            </Button>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  {detalhes.item.nome}
                  <Badge variant="outline" className="text-xs font-mono">{detalhes.item.unidade}</Badge>
                </CardTitle>
                <CardDescription>
                  Estoque: {detalhes.item.estoque_total} | Aprovisionado: {detalhes.item.aprovisionado} | Saldo: {detalhes.item.saldo_disponivel}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {detalhes.aprovisionamentos.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Paciente</TableHead>
                        <TableHead className="w-28 text-right">Quantidade</TableHead>
                        <TableHead className="w-36">Responsável</TableHead>
                        <TableHead className="w-20 text-center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detalhes.aprovisionamentos.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {p.paciente_nome}
                          </TableCell>
                          <TableCell className="text-right font-bold text-amber-600">{p.quantidade} {p.unidade}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{p.criado_por}</TableCell>
                          <TableCell className="text-center">
                            <Button variant="ghost" size="sm" className="h-7 gap-1 text-blue-600 hover:text-blue-800"
                              onClick={() => { setShowDevolver(p.id); setDevolverQty(''); setDevolverObs(''); }}
                              data-testid={`btn-devolver-${p.id}`}>
                              <Undo2 className="h-3.5 w-3.5" /> Devolver
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-muted-foreground py-6">Nenhum aprovisionamento ativo para este item</p>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          /* List view */
          <>
            <Card>
              <CardContent className="pt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por nome ou SKU..." className="pl-9" data-testid="input-search-aprov" />
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : filtered.length > 0 ? (
              <Card>
                <CardContent className="pt-0 px-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead className="w-28 text-right">Estoque</TableHead>
                        <TableHead className="w-32 text-right">Aprovisionado</TableHead>
                        <TableHead className="w-28 text-right">Saldo</TableHead>
                        <TableHead className="w-20 text-center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((item) => (
                        <TableRow key={item.id} className="cursor-pointer hover:bg-accent/50"
                          onClick={() => fetchDetalhes(item.id)}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {item.tipo === 'medicamento' ? <Pill className="h-4 w-4 text-purple-500 shrink-0" /> : <Box className="h-4 w-4 text-blue-500 shrink-0" />}
                              <div>
                                <p className="font-medium">{item.nome}</p>
                                {item.sku && <p className="text-xs font-mono text-muted-foreground">{item.sku}</p>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-bold">{item.estoque_total} <span className="text-xs font-normal text-muted-foreground">{item.unidade}</span></TableCell>
                          <TableCell className="text-right">
                            <span className={`font-bold ${item.aprovisionado > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>{item.aprovisionado}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`font-bold ${item.saldo_disponivel <= 0 ? 'text-red-600' : 'text-green-600'}`}>{item.saldo_disponivel}</span>
                          </TableCell>
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <ChevronRight className="h-4 w-4 mx-auto text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <PackageCheck className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p className="font-medium">{items.length === 0 ? 'Nenhum item aprovisionável' : 'Nenhum resultado'}</p>
                  <p className="text-sm">{items.length === 0 ? 'Clique em "Adicionar Item" para marcar itens como aprovisionáveis' : 'Tente outra busca'}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Provision Dialog */}
        <Dialog open={showProv} onOpenChange={setShowProv}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><PackageCheck className="h-5 w-5 text-primary" /> Aprovisionar Item</DialogTitle>
              <DialogDescription>Reserve itens do estoque para um paciente internado.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label>Item *</Label>
                <Combobox options={itemOptions} value={provForm.item_id}
                  onValueChange={(v) => setProvForm(f => ({ ...f, item_id: v }))}
                  placeholder="Selecionar item..." searchPlaceholder="Buscar..." emptyText="Nenhum item"
                  data-testid="select-prov-item" />
              </div>
              <div className="space-y-1">
                <Label>Paciente *</Label>
                <Combobox options={pacienteOptions} value={provForm.internacao_id}
                  onValueChange={(v) => setProvForm(f => ({ ...f, internacao_id: v }))}
                  placeholder="Selecionar paciente..." searchPlaceholder="Buscar..." emptyText="Nenhum paciente"
                  data-testid="select-prov-paciente" />
              </div>
              <div className="space-y-1">
                <Label>Quantidade *</Label>
                <Input type="number" step="1" min="1" value={provForm.quantidade}
                  onChange={(e) => setProvForm(f => ({ ...f, quantidade: e.target.value }))}
                  data-testid="input-prov-quantidade" />
              </div>
              <div className="space-y-1">
                <Label>Observações</Label>
                <Input value={provForm.observacoes}
                  onChange={(e) => setProvForm(f => ({ ...f, observacoes: e.target.value }))}
                  placeholder="Opcional..." data-testid="input-prov-obs" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowProv(false)}>Cancelar</Button>
              <Button onClick={handleAprovisionar} disabled={savingProv || !provForm.item_id || !provForm.internacao_id || !provForm.quantidade}
                className="gap-1" data-testid="btn-confirmar-prov">
                {savingProv ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Aprovisionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Return Dialog */}
        <Dialog open={!!showDevolver} onOpenChange={(v) => !v && setShowDevolver(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Undo2 className="h-5 w-5 text-blue-600" /> Devolver ao Estoque</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label>Quantidade a devolver *</Label>
                <Input type="number" step="1" min="1" value={devolverQty}
                  onChange={(e) => setDevolverQty(e.target.value)}
                  autoFocus data-testid="input-devolver-qty" />
              </div>
              <div className="space-y-1">
                <Label>Observações</Label>
                <Input value={devolverObs} onChange={(e) => setDevolverObs(e.target.value)}
                  placeholder="Opcional..." data-testid="input-devolver-obs" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDevolver(null)}>Cancelar</Button>
              <Button onClick={handleDevolver} disabled={!devolverQty} className="gap-1 bg-blue-600 hover:bg-blue-700"
                data-testid="btn-confirmar-devolver">
                <Undo2 className="h-4 w-4" /> Devolver
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Aprovisionavel Item Dialog */}
        <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Plus className="h-5 w-5" /> Adicionar Item Aprovisionável</DialogTitle>
              <DialogDescription>Selecione um item do estoque para marcar como aprovisionável.</DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Combobox
                options={allEstoqueItems.filter(i => !items.some(a => a.id === i.id)).map(i => ({
                  value: i.id,
                  label: `${i.nome} (${i.estoque_atual} ${i.unidade})`,
                  subtitle: i.tipo === 'medicamento' ? 'Medicamento' : 'Material',
                }))}
                value={addItemId}
                onValueChange={setAddItemId}
                placeholder="Selecionar item..." searchPlaceholder="Buscar..." emptyText="Todos já estão na lista"
                data-testid="select-add-aprovisionavel"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancelar</Button>
              <Button onClick={handleAddAprovisionavel} disabled={!addItemId} className="gap-1" data-testid="btn-confirmar-add">
                <Check className="h-4 w-4" /> Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Aprovisionamento;
